import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { sectionVariant, staggerContainer, itemVariant, viewportOnce } from "@/lib/motion";
import { usePageContent } from "@/hooks/usePageContent";
import { useContentItems } from "@/hooks/useContentItem";

const fallback = {
  label: "Case Studies",
  headline: "Results, not promises.",
  subheadline: "Real outcomes for real businesses. Each engagement is different, but the commitment to measurable impact is constant.",
  caseStudies: [] as any[],
};

const CaseStudies = () => {
  const { content } = usePageContent("case-studies", fallback);
  const { data: caseStudyPosts } = useContentItems("case_study");

  const studies = caseStudyPosts && caseStudyPosts.length > 0
    ? caseStudyPosts.map((post) => ({
        client: (post.content as any)?.client || post.title,
        industry: (post.content as any)?.industry || "Industry",
        challenge: post.excerpt || (post.content as any)?.challenge || "",
        result: (post.content as any)?.result || "",
        slug: post.slug,
        thumbnail: (post.content as any)?.thumbnail_image || null,
      }))
    : content.caseStudies;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-32 pb-24">
        <div className="container-wide">
          {/* Header */}
          <motion.div className="max-w-2xl mb-16" variants={sectionVariant} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            <div className="flex items-center gap-3 mb-6">
              <div className="accent-bar" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                {content.label}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
              {content.headline}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {content.subheadline}
            </p>
          </motion.div>

          {/* Case Study Cards */}
          <motion.div
            className="grid md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            {studies.map((study: any) => {
              const card = (
                <>
                  {/* Thumbnail */}
                  {study.thumbnail?.src ? (
                    <div className="aspect-[3/2] overflow-hidden bg-muted/30">
                      <img
                        src={study.thumbnail.src}
                        alt={study.thumbnail.alt || study.client}
                        width={study.thumbnail.width || 600}
                        height={study.thumbnail.height || 400}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[3/2] bg-muted/20 flex items-center justify-center">
                      <span className="text-xs text-muted-foreground/50 uppercase tracking-wider">
                        {study.industry}
                      </span>
                    </div>
                  )}

                  {/* Text */}
                  <div className="p-6">
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3 block">
                      {study.industry}
                    </span>
                    <h3 className="text-lg font-medium text-foreground leading-snug mb-2">
                      {study.client}
                    </h3>
                    {study.challenge && (
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-3">
                        {study.challenge}
                      </p>
                    )}
                    {study.result && (
                      <span className="text-sm font-semibold text-foreground">
                        {study.result}
                      </span>
                    )}
                  </div>
                </>
              );

              return (
                <motion.div key={study.client} variants={itemVariant}>
                  {study.slug ? (
                    <Link
                      to={`/case-studies/${study.slug}`}
                      className="block border border-border hover:border-primary/20 transition-colors overflow-hidden"
                    >
                      {card}
                    </Link>
                  ) : (
                    <div className="border border-border overflow-hidden">
                      {card}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>

          {studies.length === 0 && (
            <p className="text-center text-muted-foreground py-16">No case studies published yet.</p>
          )}

          {/* Soft CTA */}
          <motion.div className="mt-16 text-center" variants={sectionVariant} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            <p className="text-sm text-muted-foreground">
              Exploring a structured growth engagement?{" "}
              <Link to="/contact" className="text-foreground hover:text-primary transition-colors">
                Start a conversation
              </Link>
            </p>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudies;
